import { Box, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { Tooltip } from '@chakra-ui/tooltip';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { ClearButton } from '@morressier/alembic';
import { useNavigate } from 'react-router-dom';
import {
  adjudicationStatusBadges,
  assessmentBadges,
  BaseBadge,
  submissionStatusBadges
} from 'src/components/Review/Badges';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import { formatDeadlineDate } from 'src/utils/date';
import {
  checkOverdueDeadlineMessage,
  MAX_DAYS_TO_EXPIRE_DEADLINE
} from 'src/utils/deadlines';
interface TableColumn {
  key: string;
  label: string;
  size: number;
}
const ACTIVE_COLUMNS: TableColumn[] = [
  { key: 'title', label: 'Title', size: 2 },
  { key: 'journal', label: 'Journal', size: 2 },
  { key: 'deadline', label: 'Deadline', size: 1 },
  { key: 'adjudicationsStatus', label: 'Adjudications Status', size: 1 },
  { key: 'recommendation', label: 'Recommendation', size: 0.5 },
  { key: 'phase', label: 'Phase', size: 2 }
];

export const AdjudicationsTableHeader = () => {
  return (
    <Thead>
      <Tr
        data-testId="adjudications-tableheader"
        gap={4}
        sx={{
          '& th': {
            textTransform: 'none',
            color: 'black'
          }
        }}
      >
        {ACTIVE_COLUMNS.map(column => (
          <Th key={column.key} flex={column.size} paddingX={2}>
            <Text textStyle="headingItem" whiteSpace="nowrap">
              {column.label}
            </Text>
          </Th>
        ))}
      </Tr>
    </Thead>
  );
};

export const AdjudicationsTableRow = ({ data }: any) => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const {
    id,
    submissionCurrentWorkflowState,
    submissionContext,
    submission,
    workflow,
    caseData
  } = data;

  const adjudicationRecommendation = caseData?.adjudication?.recommendation;
  const adjudicationStatus = workflow.currentState;
  const deadlineDate = data.workflow.currentDeadline
    ? new Date(data.workflow.currentDeadline)
    : null;

  const { todaysDate, daysToDeadline } = checkOverdueDeadlineMessage(
    deadlineDate,
    adjudicationStatus
  );

  return (
    <Tr
      key={id}
      data-testId="adjudication-active-table-row"
      gap={4}
      background="white"
    >
      <Td flex={ACTIVE_COLUMNS[0]?.size}>
        <ClearButton
          ariaLabel={`open ${submission?.title} adjudications`}
          display="block"
          width="100%"
          textAlign="left"
          onClick={() =>
            navigate(`/app/adjudications/${id}?${searchParams.toString()}`)
          }
        >
          <Tooltip
            hasArrow
            borderRadius={4}
            placement="top"
            label={<RichTextPreview value={submission?.title} />}
          >
            <Box width="100%">
              <Text textStyle="headingLine" whiteSpace="normal">
                {submission?.manuscriptId}
              </Text>

              <Box isTruncated noOfLines={1} width="100%">
                <Text textStyle="textBody" whiteSpace="normal">
                  <RichTextPreview value={submission?.title} />
                </Text>
              </Box>
            </Box>
          </Tooltip>
        </ClearButton>
      </Td>
      <Td flex={ACTIVE_COLUMNS[1]?.size} textTransform="uppercase" paddingX={2}>
        {submissionContext?.data.shortname}
      </Td>
      <Td flex={ACTIVE_COLUMNS[2]?.size} textTransform="uppercase" paddingX={2}>
        <Text
          color={
            workflow?.currentDeadline !== null &&
            workflow?.currentDeadline &&
            ((deadlineDate && todaysDate >= deadlineDate) ||
              daysToDeadline <= MAX_DAYS_TO_EXPIRE_DEADLINE)
              ? 'actionSecondaryCautionContent'
              : ''
          }
        >
          {workflow?.currentDeadline !== null && workflow?.currentDeadline
            ? formatDeadlineDate(new Date(workflow?.currentDeadline))
            : 'N/A'}
        </Text>
      </Td>
      <Td flex={ACTIVE_COLUMNS[3]?.size} paddingX={2}>
        {
          adjudicationStatusBadges[
            workflow.currentState === 'assessment'
              ? 'draft'
              : workflow.currentState
          ]
        }
      </Td>
      <Td flex={ACTIVE_COLUMNS[4]?.size} paddingX={2}>
        {adjudicationRecommendation ? (
          assessmentBadges[adjudicationRecommendation]
        ) : (
          <BaseBadge icon={faXmark} variant="neutral" label="Not Applicable" />
        )}
      </Td>

      <Td flex={ACTIVE_COLUMNS[6]?.size} paddingX={2}>
        {submissionStatusBadges[submissionCurrentWorkflowState || 'initial']}
      </Td>
    </Tr>
  );
};
